import { useState, useEffect } from "react";
import { useHistory } from "react-router";
import moment from "moment";

import { Button, Typography, Select, Table, Empty, Card } from "antd";
import { PrinterOutlined } from "@ant-design/icons";

import { formatDate, formatPhoneNumber, groupArrayOfObjectsByKey, getDisplayCompanyId, sortArrayByKey, addCustomAppInsightsEvent } from "utils/helpers";
import { ErrorResponse } from "utils/types";
import notifier from "utils/notifier";
import { UserRole, LineOfBusiness, HealthCondition, DiagsStatusCode, ClickEvents } from "utils/enums";

import { GetProviderReportResponse } from "store/eligibility/gap/actions";

import FloatLabel from "components/Common/FloatLabel";

import './index.scss';

import GapMembersSectionProps from "./Proptypes";

const { Title } = Typography;
const { Option } = Select;

interface HccTableSectionProps {
  title: string;
  dataSource: any[];
  isFetching: boolean;
}

const hccSuggestionColumns = [
  {
    title: "Category",
    key: "RmiStatusDesc",
    dataIndex: "RmiStatusDesc",
    render: (_: string, record: any, __: number) => {
      return (
        <div> 
          {record.RmiStatusDesc}
        </div>
      )
    },
    sorter: (a: any, b: any) => a.hccCode.localeCompare(b.hccCode)
  },
  {
    title: "HCC Description",
    key: "hccDescription",
    dataIndex: "hccDescription",
  },
  {
    title: "DX Code",
    key: "diag",
    dataIndex: "diag",
    render: (_: string, record: any) => {        
        return (
          <span>           
            <div>
              {(record.diag3 !== '' && record.diag3 !== null) ? record.diag3 : ""}
            </div>
            <div>              
              {(record.diag2 !== '' && record.diag2 !== null) ? record.diag2 : ""}
            </div>
            <div>
              {(record.diag1 !== '' && record.diag1 !== null) ? record.diag1 : ""}
            </div> 
          </span>           
        );        
    },
    sorter: (a: any, b: any) => a.dxCode.localeCompare(b.dxCode)
  },
  {
    title: "DOS",
    key: "dos",
    dataIndex: "dos",
    render: (_: string, record: any) => {        
        return (
          <span>           
            <div>
              {(record.dos3 !== '' && record.dos3 !== 'Expected' && record.dos3 !== null) ? record.dos3 : ""}
            </div>
            <div>              
              {(record.dos2 !== '' && record.dos2 !== 'Expected' && record.dos2 !== null) ? record.dos2 : ""}
            </div>
            <div>
              {(record.dos1 !== '' && record.dos1 !== 'Expected' && record.dos1 !== null) ? record.dos1 : ""}
            </div> 
          </span>           
        );        
    }
  },
  {
    title: "Reported By",
    key: "reportedBy",
    dataIndex: "reportedBy",
    render: (_: string, record: any) => {        
        return (
          <span>           
            <div>
              {(record.reportedBy3 !== '' && record.reportedBy3 !== null) ? record.reportedBy3 : ""}
            </div>
            <div>              
              {(record.reportedBy2 !== '' && record.reportedBy2 !== null) ? record.reportedBy2 : ""}
            </div>
            <div>
              {(record.reportedBy1 !== '' && record.reportedBy1 !== null) ? record.reportedBy1 : ""}
            </div> 
          </span>           
        );        
    }
  },
];

const hccSuspectColumns = [
  {
    title: "Category",
    key: "RmiStatusDesc",
    dataIndex: "RmiStatusDesc",
    render: (_: string, record: any, __: number) => {
      return (
        <div> 
          {record.RmiStatusDesc}
        </div>
      )
    },
    sorter: (a: any, b: any) => a.hccCode.localeCompare(b.hccCode)
  },
  {
    title: "HCC Description",
    key: "hccDescription",
    dataIndex: "hccDescription",
  },
  {
    title: "DX Code",
    key: "diag",
    dataIndex: "diag",
    render: (_: string, record: any) => {        
        return (
          <span>           
            <div>
              {(record.diag3 !== '' && record.diag3 !== null) ? record.diag3 : ""}
            </div>
            <div>              
              {(record.diag2 !== '' && record.diag2 !== null) ? record.diag2 : ""}
            </div>
            <div>
              {(record.diag1 !== '' && record.diag1 !== null) ? record.diag1 : ""}
            </div> 
          </span>           
        );        
    },
    sorter: (a: any, b: any) => a.dxCode.localeCompare(b.dxCode)
  },
  {
    title: "Suspect Evidence",
    key: "comment",
    dataIndex: "comment",
    render: (_: string, record: any) => {        
        return (
          <span>           
            <div>
              {record.comment ? record.comment : ""}
            </div>
          </span>           
        );        
    }
  }
];

const GapMembersSection = (props: GapMembersSectionProps) => {

  const history = useHistory();

  const [lastRefresh, setLastRefresh] = useState('');
  const [lob, setLob] = useState<string>(LineOfBusiness.MEDICARE);
  const [hccMembersDataSource, setHccMembersDataSource] = useState<any>([{}]); //long to define and I need to access them
  const [hccConditionDataSource, setHccConditionDataSource] = useState<any>([{}]); //long to define and I need to access them
  const [hccSuspectDataSource, setSuspectedDataSource] = useState<any[]>([]);
  const [gapReportDataSource, setGapReportDataSource] = useState<GetProviderReportResponse>({
    RmiCompanyId: "",
    RmiCreatedBy: "",
    RmiCreatedDate: "",
    RmiFileNamePDF: "",
    RmiFileNameXLS: "",
    RmiModifiedBy: "",
    RmiModifiedDate: "",
    RmiProvId: "",
    RmiProvKeyId: "",
    RmiRevFullName: "",
    RmiRptType: "",
    RmiStoredFilePath: "",
    RmiUid: 0
  });

  const hccMembersColumns = [
    {
      title: 'MEMBER ID (Click on the member to see the measures in each gaps.)',
      key: 'RmiMembId',
      dataIndex: 'RmiMembId',
    },
    {
      title: 'MEMBER NAME',
      key: 'RmiRevFullName',
      dataIndex: 'RmiRevFullName',
      render: (_: string, record: any, __: number) =>
        <a href='#/' onClick={() => triggerMemberEligibilityDetailPage(record)}>
          {record.RmiRevFullName}
        </a>
    },
    {
      title: 'DATE OF BIRTH',
      key: 'RmiBirth',
      dataIndex: 'RmiBirth',
      render: (_: string, record: any, __: number) => formatDate(record.RmiBirth, 'MM/DD/YYYY')
    },
    {
      title: 'PHONE NUMBER',
      key: 'RmiPhone',
      dataIndex: 'RmiPhone',
      render: (_: string, record: any, __: number) => formatPhoneNumber(record.RmiPhone)
    },
  ];

  const hccConditionColumns = [
    {
      title: "HCC Category",
      key: "hccCode",
      dataIndex: "hccCode",
    },
    {
      title: "HCC Description",
      key: "hccDescription",
      dataIndex: "hccDescription",
    },
    {
      title: "DX Code",
      key: "diag",
      dataIndex: "diag",
      render: (_: string, record: any) => {        
          return (
            <span>           
              <div>
                {(record.diag1 !== '' && record.diag1 !== null) ? record.diag1 : ""}
              </div>
              <div>              
                {(record.diag2 !== '' && record.diag2 !== null) ? record.diag2 : ""}
              </div>
              <div>
                {(record.diag3 !== '' && record.diag3 !== null) ? record.diag3 : ""}
              </div> 
            </span>           
          );        
      },
      sorter: (a: any, b: any) => a.dxCode.localeCompare(b.dxCode)
    },
    {
      title: moment().year() - 2, //dynamic year. Gets 2 years before
      key: "RmiReportedDt1",
      dataIndex: "RmiReportedDt1",
    },
    {
      title: moment().year() - 1, //dynamic year. Gets 1 year before
      key: "RmiReportedDt2",
      dataIndex: "RmiReportedDt2",
    },
    {
      title: moment().year(), //dynamic year. Current year
      key: "RmiReportedDt3",
      dataIndex: "RmiReportedDt3",
    },
  ];

  //member name links to eligibility info page
  const triggerMemberEligibilityDetailPage = (data: Object) => {
    search(data);
  };

  const search = (memberInfo: any) => {

    //According to legacy code
    const provider = (sessionStorage.user_role === UserRole.CASE || sessionStorage.user_role === UserRole.IPA) ? "OTHER" : "PCP";
    let formData = {
      "DateOfBirth": memberInfo.RmiBirth,
      "MemberId": memberInfo.RmiMembId,
      "Provider": provider,
      "UserId": sessionStorage.user_id
    }

    return props.search(formData)
      .then((response: any) => {
        if (response.length > 0) {
          history.push({
            pathname: '/eligibility/member-detail',
            state: {
              eligibilityMemberInfo: response[0]
            }
          });
        } else {
          notifier.error('No access. Please use eligibility to search for this member.');
        }
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err?.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
      });
  };

  //get the last refresh of the hcc member in the gap report (top right corner of page)
  const getLastRunDate = () => {
    const providerObject = JSON.parse(props.selectedProvider); //using the parent selectedProvider state
    return props.getLastRunDate(providerObject.rmiProvKeyId, providerObject.rmiCompanyId, 'hcc')
      .then((response: string) => {
        setLastRefresh(formatDate(response, 'MM/DD/YYYY [at] h:mmA'));
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err.response?.data.Message || 'Something went wrong, please contact a system administrator.');
      });
  }

  //print HCC summary
  const printProviderMemberReport = () => {
    const providerObject = JSON.parse(props.selectedProvider); //using the parent selectedProvider state
    return props.printProviderMemberReport(providerObject.rmiProvKeyId, providerObject.rmiCompanyId, lob, sessionStorage.user_id)
      .then((response: any) => {
        const file = new Blob([response], {type: 'application/pdf'});
        const fileURL = URL.createObjectURL(file);
        window.open(fileURL, '_blank');
        notifier.success("Download was successful.");
      })
      .catch((err: ErrorResponse) =>
        notifier.error(err.response.data.Message || 'Something went wrong, please contact a system administrator.')
      );
  }

  //Member ID table
  const getProviderHccMembersLob = () => {
    const providerObject = JSON.parse(props.selectedProvider); //using the parent selectedProvider state
    return props.getProviderHccMembersLob(providerObject.rmiProvKeyId, lob, sessionStorage.user_id, providerObject.rmiCompanyId)
      .then((response: any) => {
        setHccMembersDataSource(response.map((element: any, index: number) => ({ key: index.toString(), ...element })));
      })
      .catch((err: ErrorResponse) => {
        setHccMembersDataSource("");
        notifier.error(err.response.data.Message || 'Something went wrong, please contact a system administrator.');
      });
  }

  //get GAP reports at the bottom of the page
  const getProviderReport = () => {
    const providerObject = JSON.parse(props.selectedProvider); //using the parent selectedProvider state
    let vendorId = '';
    return props.getProviderReport(providerObject.rmiProvKeyId, providerObject.rmiCompanyId, sessionStorage.user_id, 'HCC', moment().year(), vendorId)
      .then((response: any) => {
        if (response == null || response == '') {
          response.RmiCompanyId = providerObject.rmiCompanyId;
          response.RmiRevFullName = providerObject.rmiRevFullName;
          response.RmiProvId = providerObject.RmiProvId;
        }
        setGapReportDataSource(response);
      })
      .catch((err: ErrorResponse) =>
        notifier.error(err.response.data.Message || 'Something went wrong, please contact a system administrator.')
      );
  }

  const [collapseState, setCollapseState] = useState<any>([]);

  //TODO This should be moved somewhere to be reuseable.
  //It's also used in Medicare HCC tab under Search Eligibility
  const claimDate = (date: string, severity?: string) => {
    if (!date && !severity) return '';
    else if (!date && severity && severity === HealthCondition.CHRONIC) return 'Expected'; //This is used for the 3rd year only
    else if (!date && severity && severity === HealthCondition.SUSPECTED) return 'Suspected'; //This is used for the 3rd year only
    else if (!date && severity && severity === HealthCondition.ACUTE) return 'Informational'; //This is used for the 3rd year only
    else if(!moment(date).isValid()) return date;

    return moment(date).format('MM/DD/YYYY');
  }

  const processItems = (items: any, destinationArray: any, companyId: string, membMpiNo: any, hccCode: any ) => {
    items.forEach((item: any) => {
      let dxCode = item.RmiDiagCode10 + "\n";
      destinationArray.push({
        key: hccCode,
        companyId,
        membMpiNo,
        hccCode,
        comment: item.RmiComment,
        RmiStatusDesc: item.RmiStatusDesc,
        hccDescription: item.RmiDescription,
        dxCode,
        dos1: claimDate(item.RmiReportedDt1),
        dos2: claimDate(item.RmiReportedDt2),
        dos3: claimDate(item.RmiReportedDt3),
        reportedBy1: item.RmiReportedBy1,
        reportedBy2: item.RmiReportedBy2,
        reportedBy3: item.RmiReportedBy3,
        claimId1: item.ClaimID1,
        claimId2: item.ClaimID2,
        claimId3: item.ClaimID3,
        year1: item.yr1,
        year2: item.yr2,
        year3: item.yr3,
        diag1: item.diag1,
        diag2: item.diag2,
        diag3: item.diag3,
      });
    });
  };
  
  const handleRowExpand = (expanded: any, record: any) => {
    //if it's a different row key, then call the API and expand the accordion
    const keys = [];

    if (expanded) {
      props.getMemberMeasures(record.RmiMembMpiNo, record.RmiProvKeyId, record.RmiCompanyId, lob, sessionStorage.user_id)
        .then((response: any) => {

          const companyId = record.RmiCompanyId;
          const membMpiNo = record.RmiMembMpiNo;

          const hccSuggestionResults = response;
          const groupedHccSuggestions = groupArrayOfObjectsByKey(
            hccSuggestionResults,
            "RmiHccCode"
          );

          const groupedHccSuggestionResults: any[] = [];
          const groupedHccSuspectResults: any[] = [];

          // Process and filter in one step
          for (const hccCode in groupedHccSuggestions) {
            const openItems = groupedHccSuggestions[hccCode].filter(
              (item: any) => item.RmiStatusDesc === "Open" || item.RmiStatusDesc === "Closed"
            );
            processItems(
              openItems,
              groupedHccSuggestionResults,
              companyId,
              membMpiNo,
              hccCode
            );

            const suspectItems = groupedHccSuggestions[hccCode].filter(
              (item: any) => item.RmiStatusDesc === "Suspected"
            );
            processItems(
              suspectItems,
              groupedHccSuspectResults,
              companyId,
              membMpiNo,
              hccCode
            );
          }

          const sortedHccSuggestions = sortArrayByKey(
            groupedHccSuggestionResults,
            "key"
          );

          setHccConditionDataSource(sortedHccSuggestions);
          setSuspectedDataSource(groupedHccSuspectResults);
        })
        .catch((err: ErrorResponse) => {
          console.error(err.response.data.Message || 'Something went wrong, please contact a system administrator.')
        });

        //This currently only allows to expand one at a time and update the state to manage 1 row (historical condition) at a time
        keys.push(record.key);
    }
    setCollapseState(keys);
  }

  //export excel API
  const getDownloadDocument = (extension: string) => {
    const providerObject = JSON.parse(props.selectedProvider);
    const model = {
      "FileName": "HCC_GapReport." + extension,
      "FilePath": "",
      "CompanyId": providerObject.rmiCompanyId,
      "ProviderId": providerObject.RmiProvId,
      "ProviderName": providerObject.rmiRevFullName,
      "MeasureYear": moment().year().toString(),
      "ReportType": "HCC"
    }
    addCustomAppInsightsEvent(ClickEvents.DOWNLOAD_HCC_GAP_REPORT, model);

    return props.getDownloadDocument(model)
      .then((res: ArrayBuffer) => {
        let fileType;
        if (extension === "XLSX") {
          fileType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
        } else {
          fileType = "application/pdf";
        }
        let blob = new Blob([res], {type: fileType});
        const fileURL = URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.download = model.FileName;
        anchor.href = fileURL;
        anchor.click();

        notifier.success("File exported successfully.");
      })
      .catch((err: ErrorResponse) =>
        notifier.error(err.response.data.Message || 'File download failed, please contact a system administrator.')
      );
  }

  const HccTableSection: React.FC<HccTableSectionProps> = ({ title, dataSource, isFetching }) => {
    // Determine which columns to use based on the title
    const tableColumns = title === "Suspected HCCs" ? hccSuspectColumns : hccSuggestionColumns;
  
    return (
      <div className="print-hcc-section">
        <div className="row custom-mt">
          <div className="col-lg-24 col-md-24 col-sm-24">
            <Title level={5}>{title}</Title>
          </div>
        </div>
  
        <Table
          className="mb-5"
          columns={tableColumns}
          dataSource={dataSource}
          pagination={{ position: ["bottomCenter"], hideOnSinglePage: true }}
          locale={{ emptyText: (
            <p className="default-no-result text-center" style={{color: 'red'}}>
              <i className="fa fa-exclamation-circle mr-1"></i>
              {`There were no ${title.toLowerCase()} received for this member. Please review the member's chart.`}
            </p>
          )}}
          loading={isFetching}
        />
      </div>
    );
  }; 

  useEffect(() => {
    //reruns the APIs below if parent "selectedProvider" state from the dropdown or lob state from the dropdown are updated
    if (props.selectedProvider?.length > 0) {
      //run the MemberID table API
      getProviderHccMembersLob();

      //run the last run date API
      getLastRunDate();

      //run the Gap Report table API
      getProviderReport();
    }
  }, [props.selectedProvider, lob]);

  return (
    <>
      <div className="row">
        <div className="col mt-2">
          <FloatLabel label="MEMBERS Measures for:" value={props.selectedProvider}>
            <Select
              showSearch
              value={props.selectedProvider}
              placeholder="Provider"
              onChange={value => props.selectedProviderHandler(value)}
              loading={props.isFetchingEnabledList}
            >
              {props.providerList.map((option: any, index: number) => {
                return <Option key={index} value={JSON.stringify(option)}>{option.Provider}</Option>;
              })}
            </Select>
          </FloatLabel>
        </div>
        <div className="col mt-2">
          <FloatLabel label="Line of Business:" value={lob}>
            <Select
              showSearch
              value={lob}
              placeholder=""
              onChange={value => setLob(value)}
              loading={props.isFetchingEnabledList}
            >
              <Option key={1} value={LineOfBusiness.MEDICARE}>{LineOfBusiness.MEDICARE}</Option>
            </Select>
          </FloatLabel>
        </div>
        <div className="col text-right">
          <div className="row">Trend: Year-to-Date</div>
          <div className="row">
            <Button
              type="primary"
              onClick={() => getDownloadDocument("XLSX")}
              icon={<PrinterOutlined />}
              disabled={hccMembersDataSource.length === 0}
              loading={props.isFetchingProviderHcc || props.isFetchingProvider || props.isFetchingLastRun}
            >
              Print HCC Summary
            </Button>
          </div>
        </div>
      </div>
      <div className="row mt-5 member-id-table">
        <Table
          columns={hccMembersColumns}
          dataSource={hccMembersDataSource}
          pagination={false}
          expandable={{
            expandedRowRender: record =>
              (<>
                <Card title="Historical And Suspected HCC Conditions" style={{ width: '100%' }}>
                <HccTableSection 
                    title="Historical HCCs" 
                    dataSource={hccConditionDataSource} 
                    isFetching={props.isFetchingProviderHcc}
                  />
                  <HccTableSection 
                    title="Suspected HCCs" 
                    dataSource={hccSuspectDataSource} 
                    isFetching={props.isFetchingProviderHcc}
                  />
                </Card>
              </>),
          }}
          onExpand={(expanded, record) => handleRowExpand(expanded, record)}
          expandedRowKeys={collapseState}
          loading={props.isFetchingProviderHcc}
        />
      </div>
    </>
  );
};

export default GapMembersSection;
